
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import "./ArrivalDepartureListItem.scss";
import { ArrivalDeparture } from "../models/ArrivalDeparture";
import Passenger from "../../Passenger/models/Passenger";
import Utility from "../../Passenger/models/Utility";
import { Helpers } from "../../../util/Helpers";
import { Guid } from "guid-typescript";
import vuetifyToast from "vuetify-toast";
import { RolePermission } from "@/models/Role";
import moment from "moment";

@Component({
  components: {}
})
export class ArrivalDepartureListItem extends Vue {
  @Prop({ required: true })
  arrivalsDepartures: ArrivalDeparture[];

  @Prop({ default: "" })
  search: string;

  dayIsToday: boolean = false;

  get primaryArrivalDeparture(): ArrivalDeparture {
    return this.arrivalsDepartures[0];
  }

  get arrivalsDeparturesFiltered(): ArrivalDeparture[] {
    return this.arrivalsDepartures.filter(ad => {
      const exists = this.arrivalDeparturePassenger(ad);
      return exists;
    });
  }

  get arrivalsDeparturesIds(): Guid[] {
    return this.arrivalsDepartures.map((te: ArrivalDeparture) => te.id);
  }

  get cancelledArrivalDepartures(): ArrivalDeparture[] {
    return this.arrivalsDepartures.filter(te => te.cancelled);
  }

  get cancellingArrivalDepartures(): ArrivalDeparture[] {
    return this.arrivalsDepartures.filter(te => te.cancelling);
  }

  get passengerIds(): number[] {
    return this.arrivalsDepartures.map((te: ArrivalDeparture) => te.passengerId);
  }

  get cancelledPassengerIds(): number[] {
    return this.cancelledArrivalDepartures.map((te: ArrivalDeparture) => te.passengerId);
  }

  get cancellingPassengerIds(): number[] {
    return this.cancellingArrivalDepartures.map((te: ArrivalDeparture) => te.passengerId);
  }

  get storePassengers(): Passenger[] {
    return this.$store.state.passenger.arrivalDeparturePassengers;
  }

  

  arrivalDeparturePassenger(arrivalDeparture: ArrivalDeparture): Passenger {
    let passenger = this.storePassengers.find(p => p.id === arrivalDeparture.passengerId);
    // If store did not contain passenger create fake passenger with unknown name and return it
    if (!passenger) {
      const name = arrivalDeparture.passengerName 
        || this.$i18n.translate("passenger.unknown_name", "Unknown passenger");

      passenger = new Passenger({ id: arrivalDeparture.passengerId, name });
    }
    return passenger;
  }

  // get passengers(): Passenger[] {
  //   const passengersFromDepartures = [];
  //   this.arrivalsDepartures.map(arr => {
  //     const passenger = this.storePassengers.find(p => p.id === arr.passengerId);
  //     passengersFromDepartures.push(passenger);
  //   });
  //   return passengersFromDepartures;
  // }

  get allArrivalDeparturesCancelled(): boolean {
    return this.arrivalsDepartures.every(te => te.cancelled);
  }

  get helpers(): Helpers {
    return Helpers;
  }

  get routeDestinationId(): number {
    return parseInt(this.$store.state.route.params.destinationId, 10);
  }

  get me() {
    return this.$store.state.user.me;
  }

  get userCanMarkArrivalDeparturesCompleted(): boolean {
    return this.me
      ? this.me.hasPermissions(this.routeDestinationId, [
          RolePermission.UpdateArrivalDepartures
        ])
      : false;
  }

  get routeDateId(): string | undefined {
    return this.$store.state.route.params.dateId;
  }

  utilityTitle(utilityId: number): string {
    const utility: Utility = Passenger.utilities.find(
      (u: any) => u.id === utilityId
    );
    return utility
      ? this.$i18n.translate("utility.title." + utility.title, utility.title)
      : "Unknown utility";
  }

  utilityIconImageSrc(utilityId: number): string {
    const imageBasePath = "/frontend/utilities/";
    if (utilityId === 2) {
      return imageBasePath + "man-waving-arm-32" + ".png";
    } else {
      return imageBasePath + "utility" + utilityId + ".png";
    }
  }

  isArrivalDepartureCancelledForPassenger(passenger: Passenger): boolean {
    return this.cancelledPassengerIds.includes(passenger.id);
  }

  isArrivalDepartureCancellingForPassenger(passenger: Passenger): boolean {
    const isCancelling = this.cancellingPassengerIds.includes(passenger.id);
    const isCancelled = this.isArrivalDepartureCancelledForPassenger(passenger);
    return (isCancelling && !isCancelled);
  }

  get hasPassengersMatchingSearch(): boolean {
    return (this.search && this.storePassengers.some(p => p.nameMatchesSearch(this.search)));
  }

  atOtherTimeForPassenger(passenger: Passenger): string {
    const arrivalDeparture = this.arrivalDepartureForPassenger(passenger);
    return arrivalDeparture.atOther ? arrivalDeparture.atOtherHHMM : "";
  }

  arrivalDepartureForPassenger(passenger: Passenger): ArrivalDeparture {
    return this.arrivalsDepartures.find(ad => ad.passengerId === passenger.id);
  }

  atOtherMessageForPassenger(passenger: Passenger): string {
    if (!passenger.id || !passenger.movitId) {
      return this.$i18n.translate(
        "arrival_departure.no_access_passenger_time",
        "Access to passenger information invalid"
      );
    }

    const arrivalDeparture = this.arrivalsDepartures.find(ad => ad.passengerId === passenger.id);

    if (arrivalDeparture.atOther) {
      return arrivalDeparture.isArrival
        ? this.$i18n.translate(
            "arrival_departure.departed_at_time",
            "Departed at {time}",
            {
              time: arrivalDeparture.atOtherHHMM
            }
          )
        : this.$i18n.translate(
            "arrival_departure.arrives_at_time",
            "Will arrive at {time}",
            {
              time: arrivalDeparture.atOtherHHMM
            }
          );
    } else {
      return arrivalDeparture.isArrival
        ? this.$i18n.translate(
            "arrival_departure.departed_at_unknown",
            "Departure time unknown"
          )
        : "?"
    }
  }

  mounted() {
    this.dayIsToday = moment().isSame(this.routeDateId, "day");
  }

  onPassengerClick(event: Event, passenger: Passenger, arrivalDeparture: ArrivalDeparture) {
    this.$emit("passengerClick", event, passenger, arrivalDeparture);
  }

  onPassengerSelectClick(event: Event, passenger: Passenger, arrivalDeparture: ArrivalDeparture) {
    if (passenger.inactive) {
      return;
    }
    if (!this.isArrivalDepartureCancelledForPassenger(passenger) && this.dayIsToday) {
      if (this.userCanMarkArrivalDeparturesCompleted) {
        this.$emit("passengerSelectClick", event, passenger, arrivalDeparture);
      }
    }
  }
}

export default ArrivalDepartureListItem;
